import React, { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  BooleanField,
  TopToolbar,
  FunctionField,
} from 'react-admin';
import { PageWrapper } from '../../components/PageWrapper';
import { Chip, Box, Typography, Button } from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import AddIcon from '@mui/icons-material/Add';
import { InsuranceCreateDialog } from './InsuranceCreateDialog';
import { InsuranceEditDialog } from './InsuranceEditDialog';

const ListActions = ({ onCreateClick }: { onCreateClick: () => void }) => (
  <TopToolbar>
    <Button
      variant="contained"
      startIcon={<AddIcon />}
      onClick={onCreateClick}
    >
      Create
    </Button>
  </TopToolbar>
);

const ExpiryStatusField = ({ record }: any) => {
  if (!record || !record.expiryDate) return null;

  const expiryDate = new Date(record.expiryDate);
  const today = new Date();
  const daysUntilExpiry = Math.floor((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  let color: 'error' | 'warning' | 'success' = 'success';
  let label = 'Active';

  if (daysUntilExpiry < 0) {
    color = 'error';
    label = 'Expired';
  } else if (daysUntilExpiry <= 30) {
    color = 'warning';
    label = `Expires in ${daysUntilExpiry} days`;
  } else if (daysUntilExpiry <= 60) {
    color = 'warning';
    label = `Expires in ${daysUntilExpiry} days`;
  }

  return <Chip label={label} color={color} size="small" />;
};

const Empty = ({ onCreateClick }: { onCreateClick: () => void }) => {
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 12,
        px: 3,
      }}
    >
      <ShieldIcon
        sx={{
          fontSize: 120,
          color: '#e0e0e0',
          mb: 3,
        }}
      />
      <Typography variant="h5" gutterBottom fontWeight="medium" color="text.secondary">
        No Insurance Policies Yet
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
        Track coverage, expiry dates, and deductibles all in one place.
      </Typography>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={onCreateClick}
        size="large"
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          px: 4,
          py: 1.5,
        }}
      >
        Add Your First Insurance Policy
      </Button>
    </Box>
  );
};

export const InsuranceList = () => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingInsuranceId, setEditingInsuranceId] = useState<string | null>(null);

  const handleRowClick = (id: string) => {
    setEditingInsuranceId(id);
    setEditDialogOpen(true);
    return false; // Prevent default navigation
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingInsuranceId(null);
  };

  return (
    <PageWrapper title="Insurance Policies">
      <List
        actions={<ListActions onCreateClick={() => setCreateDialogOpen(true)} />}
        sort={{ field: 'expiryDate', order: 'ASC' }}
        empty={<Empty onCreateClick={() => setCreateDialogOpen(true)} />}
      >
        <Datagrid rowClick={handleRowClick} bulkActionButtons={false}>
          <TextField source="policyNumber" label="Policy Number" />
          <TextField source="insurerName" label="Insurer" />
          <TextField source="policyName" label="Policy Name" />
          <DateField source="effectiveDate" label="Effective Date" />
          <DateField source="expiryDate" label="Expiry Date" />
          <FunctionField
            label="Status"
            render={ExpiryStatusField}
          />
          <FunctionField
            label="Liability Limit"
            render={(record: any) => {
              if (!record || !record.liabilityLimit) return '-';
              const amount = typeof record.liabilityLimit === 'object'
                ? parseFloat(record.liabilityLimit.toString())
                : parseFloat(record.liabilityLimit);

              // Check if amount is NaN or invalid
              if (isNaN(amount) || !isFinite(amount)) return '-';

              return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(amount);
            }}
          />
          <BooleanField source="active" />
        </Datagrid>
      </List>
      <InsuranceCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
      <InsuranceEditDialog
        open={editDialogOpen}
        onClose={handleEditClose}
        insuranceId={editingInsuranceId}
      />
    </PageWrapper>
  );
};
